import { EpdVersionHistoryData } from 'components/v2/compiler/version-history/utils/EpdVersionHistoryDataContext';
import { CONSTRUCTION_PCR_V2_ID } from 'constants/constants';
import { useEffect } from 'react';
import {
  useEPDDefinitionInformation,
  useEpdOrganizationInformation,
  useEpdOrganizationInformationAddress,
  useGetEpdPCRInformation,
  useGetEpdLcaSupport,
  useEpdReferenceFlow,
  useEpdConversionFactor,
  useGetEpdThirdPartyVerification,
  useEpdVerificationReportInfo,
  useEpdAdditionalInformation,
  useEpdCoverPageInformation,
  useEpdProductInformation,
  useEpdCpcrSpecificData,
  useEpdContentDeclarationProducts,
  useGetInfrastructureAndCapitalGoods,
  useGetEpdDataQualityAssessment,
  useGetLCASpecification,
  useGetEpdCO2UptakeAssociation,
  useEpdSystemBoundaryData,
  useEpdSystemBoundaryGrid,
  useEpdScenarios,
  useEpdLcaResults,
  useEpdLcaResultsAdditional,
  useEpdLcaCustomResults,
  useEpdDataSources,
  useCountriesLookup,
  useDictionaryValues,
  useUnCpcCodes,
  useMaterialPropertiesLookup,
  useEpdDataSourceOptions,
  useLCASoftwareOptions,
  useReferencePackageVersions15804,
  useRegionsLookup,
} from 'services/api/queries';

export function useEpdVersionData(versionId: string, onLoaded?: () => void) {
  const epdDefinitionInformationQuery = useEPDDefinitionInformation(versionId);
  const organizationInformationQuery = useEpdOrganizationInformation(versionId);
  const organizationInformationAddressQuery = useEpdOrganizationInformationAddress(versionId);
  const pcrInformationQuery = useGetEpdPCRInformation(versionId);
  const lcaSupportInformationQuery = useGetEpdLcaSupport(versionId);
  const referenceFlowQuery = useEpdReferenceFlow(versionId);
  const conversionFactorQuery = useEpdConversionFactor(versionId);
  const thirdPartyVerificationQuery = useGetEpdThirdPartyVerification(versionId);
  const verificationReportInfoQuery = useEpdVerificationReportInfo(versionId);
  const additionalInformationQuery = useEpdAdditionalInformation(versionId);
  const coverPageInformationQuery = useEpdCoverPageInformation(versionId);
  const productsInformationQuery = useEpdProductInformation(versionId);
  const cpcrSpecificDataQuery = useEpdCpcrSpecificData(versionId);
  const contentDeclarationProductsQuery = useEpdContentDeclarationProducts(versionId);
  const infrastructureAndCapitalGoodsQuery = useGetInfrastructureAndCapitalGoods(versionId);
  const dataQualityAssessmentAndReferenceYearsQuery = useGetEpdDataQualityAssessment(versionId);
  const lcaSpecificationQuery = useGetLCASpecification(versionId);
  const cO2UptakeAssociationQuery = useGetEpdCO2UptakeAssociation(versionId);
  const systemBoundaryQuery = useEpdSystemBoundaryData(versionId);
  const systemBoundaryGridQuery = useEpdSystemBoundaryGrid(versionId);
  const scenariosQuery = useEpdScenarios(versionId);
  const lcaResultsQuery = useEpdLcaResults(versionId);
  const lcaResultsAdditionalQuery = useEpdLcaResultsAdditional(versionId);
  const lcaCustomResultsQuery = useEpdLcaCustomResults(versionId);
  const dataSourcesQuery = useEpdDataSources(versionId);
  const countriesQuery = useCountriesLookup();
  const regionsQuery = useRegionsLookup();
  const dictionariesQuery = useDictionaryValues();
  const cpcCodesQuery = useUnCpcCodes({});
  const materialPropertiesLookupQuery = useMaterialPropertiesLookup();
  const dataSourceOptionsQuery = useEpdDataSourceOptions();
  const lcaSoftwareOptionsQuery = useLCASoftwareOptions();
  const referencePackageVersions15804Query = useReferencePackageVersions15804();

  const isShowingLoadingSkeleton =
    !epdDefinitionInformationQuery.data ||
    !organizationInformationQuery.data ||
    !organizationInformationAddressQuery.data ||
    !pcrInformationQuery.data ||
    !lcaSupportInformationQuery.data ||
    referenceFlowQuery.isPending ||
    conversionFactorQuery.isPending ||
    !thirdPartyVerificationQuery.data ||
    !verificationReportInfoQuery.data ||
    additionalInformationQuery.isPending ||
    !coverPageInformationQuery.data ||
    !productsInformationQuery.data ||
    !cpcrSpecificDataQuery.data ||
    !contentDeclarationProductsQuery.data ||
    infrastructureAndCapitalGoodsQuery.isPending ||
    !dataQualityAssessmentAndReferenceYearsQuery.data ||
    lcaSpecificationQuery.isPending ||
    !cO2UptakeAssociationQuery.data ||
    systemBoundaryQuery.isPending ||
    !systemBoundaryGridQuery.data ||
    !scenariosQuery.data ||
    !lcaResultsQuery.data ||
    !lcaResultsAdditionalQuery.data ||
    !lcaCustomResultsQuery.data ||
    !dataSourcesQuery.data ||
    !countriesQuery.data ||
    !regionsQuery.data ||
    !dictionariesQuery.data ||
    !cpcCodesQuery.data ||
    !materialPropertiesLookupQuery.data ||
    !dataSourceOptionsQuery.data ||
    !lcaSoftwareOptionsQuery.data ||
    !referencePackageVersions15804Query.data;

  const dataForContext: EpdVersionHistoryData | null = !isShowingLoadingSkeleton
    ? {
        epdVersionId: versionId,
        epdDefinitionInformation: epdDefinitionInformationQuery.data,
        organizationInformation: organizationInformationQuery.data,
        organizationInformationAddress: organizationInformationAddressQuery.data,
        pcrInformation: pcrInformationQuery.data,
        lcaSupportInformation: lcaSupportInformationQuery.data,
        referenceFlow: referenceFlowQuery.data,
        conversionFactor: conversionFactorQuery.data,
        thirdPartyVerification: thirdPartyVerificationQuery.data,
        verificationReportInfo: verificationReportInfoQuery.data,
        additionalInformation: additionalInformationQuery.data,
        coverPageInformation: coverPageInformationQuery.data,
        productsInformation: productsInformationQuery.data,
        cpcrSpecificData: cpcrSpecificDataQuery.data,
        contentDeclarationProducts: contentDeclarationProductsQuery.data,
        infrastructureAndCapitalGoods: infrastructureAndCapitalGoodsQuery.data,
        dataQualityAssessmentAndReferenceYears: dataQualityAssessmentAndReferenceYearsQuery.data,
        lcaSpecification: lcaSpecificationQuery.data,
        cO2UptakeAssociation: cO2UptakeAssociationQuery.data,
        systemBoundary: systemBoundaryQuery.data,
        systemBoundaryGrid: systemBoundaryGridQuery.data,
        scenarios: scenariosQuery.data,
        lcaResults: lcaResultsQuery.data,
        lcaCustomResults: lcaCustomResultsQuery.data,
        dataSources: dataSourcesQuery.data,
        countries: countriesQuery.data,
        regions: regionsQuery.data,
        dictionaries: dictionariesQuery.data,
        cpcCodes: cpcCodesQuery.data,
        materialPropertiesLookup: materialPropertiesLookupQuery.data,
        dataSourceOptions: dataSourceOptionsQuery.data,
        lcaSoftwareOptions: lcaSoftwareOptionsQuery.data,
        referencePackageVersions15804: referencePackageVersions15804Query.data,
        isPcrV2: !pcrInformationQuery.data
          ? undefined
          : !pcrInformationQuery.data.pcr || pcrInformationQuery.data.pcr.id === CONSTRUCTION_PCR_V2_ID,
      }
    : null;

  const isPcrV2 = !pcrInformationQuery.data
    ? undefined
    : !pcrInformationQuery.data.pcr || pcrInformationQuery.data.pcr.id === CONSTRUCTION_PCR_V2_ID;

  useEffect(() => {
    if (!isShowingLoadingSkeleton) {
      onLoaded?.();
    }
  }, [isShowingLoadingSkeleton, onLoaded]);

  return { dataForContext, isShowingLoadingSkeleton, isPcrV2 };
}
